import React from 'react';
import useSWR from 'swr';
import { fetcher } from '../../../services/fetcher';

interface SatisfactionData {
  averageRating: number;
  totalResponses: number;
  trend: 'up' | 'down' | 'stable';
  trendPercentage: number;
  byCategory?: Array<{
    category: string;
    rating: number;
    count: number;
  }>;
}

const ClientSatisfactionMetrics: React.FC = () => {
  const { data, isLoading } = useSWR<SatisfactionData>(
    '/dashboard/client-satisfaction',
    fetcher,
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
    }
  );

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Client Satisfaction</h2>
        <div className="h-[200px] flex items-center justify-center">
          <div className="animate-pulse text-gray-400">Loading data...</div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Client Satisfaction</h2>
        <div className="h-[200px] flex items-center justify-center text-gray-500">
          <div className="text-center">
            <div className="text-4xl mb-2">📊</div>
            <div>No satisfaction data available</div>
            <div className="text-sm text-gray-400 mt-2">
              Satisfaction metrics will appear here when available
            </div>
          </div>
        </div>
      </div>
    );
  }

  const { averageRating, totalResponses, trend, trendPercentage, byCategory } = data;

  const getRatingColor = (rating: number) => {
    if (rating >= 4) return 'text-green-600';
    if (rating >= 3) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getRatingBgColor = (rating: number) => {
    if (rating >= 4) return 'bg-green-100';
    if (rating >= 3) return 'bg-yellow-100';
    return 'bg-red-100';
  };

  const getTrendIcon = () => {
    switch (trend) {
      case 'up':
        return (
          <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
          </svg>
        );
      case 'down':
        return (
          <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
          </svg>
        );
      default:
        return (
          <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14" />
          </svg>
        );
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">Client Satisfaction</h2>

      <div className="space-y-4">
        {/* Main Rating */}
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm text-gray-600 mb-1">Average Rating</div>
            <div className={`text-4xl font-bold ${getRatingColor(averageRating)}`}>
              {averageRating.toFixed(1)}
              <span className="text-2xl text-gray-400">/5.0</span>
            </div>
          </div>
          <div className={`px-4 py-3 rounded-lg ${getRatingBgColor(averageRating)}`}>
            <div className="flex items-center gap-2">
              {getTrendIcon()}
              <div>
                <div className="text-xs text-gray-600">Trend</div>
                <div className={`text-sm font-semibold ${trend === 'up' ? 'text-green-600' : trend === 'down' ? 'text-red-600' : 'text-gray-600'}`}>
                  {trendPercentage > 0 ? '+' : ''}{trendPercentage.toFixed(1)}%
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Total Responses */}
        <div className="pt-3 border-t border-gray-200">
          <div className="text-sm text-gray-600">
            Based on <span className="font-semibold text-gray-900">{totalResponses}</span> responses
          </div>
        </div>

        {/* Category Breakdown */}
        {byCategory && byCategory.length > 0 && (
          <div className="pt-3 border-t border-gray-200 space-y-3">
            <div className="text-sm font-semibold text-gray-900">By Category</div>
            {byCategory.map((category) => (
              <div key={category.category} className="space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700 capitalize">{category.category}</span>
                  <span className="text-sm font-medium text-gray-900">
                    {category.rating.toFixed(1)} ({category.count})
                  </span>
                </div>
                <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${getRatingColor(category.rating).replace('text-', 'bg-')}`}
                    style={{ width: `${(category.rating / 5) * 100}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Star Rating Visual */}
        <div className="pt-3 border-t border-gray-200">
          <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((star) => (
              <svg
                key={star}
                className={`w-6 h-6 ${
                  star <= Math.round(averageRating)
                    ? 'text-yellow-400 fill-current'
                    : 'text-gray-300'
                }`}
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientSatisfactionMetrics;
